/**
 * ResultsList — 变体卡同步组件
 * 不继承 PaginatedList，避免与服务端变体分页冲突
 * 分页完全由 Liquid vpage= 参数控制
 */
class ResultsList extends HTMLElement {
  #isSyncingVariantCards = false;
  #mutationSyncFrame = 0;

  #variantCardObserver = new MutationObserver((mutations) => {
    if (this.#isSyncingVariantCards) return;
    for (const mutation of mutations) {
      if (mutation.type !== 'childList') continue;
      if (mutation.addedNodes.length === 0 && mutation.removedNodes.length === 0) continue;
      if (this.#mutationSyncFrame) return;
      this.#mutationSyncFrame = requestAnimationFrame(() => {
        this.#mutationSyncFrame = 0;
        if (!this.isConnected) return;
        this.#syncVariantCards();
      });
      break;
    }
  });

  connectedCallback() {
    this.#syncVariantCards();
    this.#observeVariantCardGrid();
    this.addEventListener('click', this.#handleCardClick);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleCardClick);
    this.#variantCardObserver.disconnect();
    if (this.#mutationSyncFrame) {
      cancelAnimationFrame(this.#mutationSyncFrame);
      this.#mutationSyncFrame = 0;
    }
  }

  /** @param {MouseEvent} event */
  #handleCardClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('a, button, input, select, textarea, label, summary, [role="button"]')) return;
    const card = event.target.closest('.product-grid__item[data-variant-id]');
    if (!(card instanceof HTMLElement)) return;
    const targetUrl = card.dataset.variantUrl || card.dataset.productUrl;
    if (!targetUrl) return;
    window.location.assign(targetUrl);
  };

  #syncVariantCards(root = this) {
    if (this.#isSyncingVariantCards) return;
    this.#isSyncingVariantCards = true;

    const cards = root.querySelectorAll('.product-grid__item[data-variant-id]');

    try {
      for (const card of cards) {
        if (!(card instanceof HTMLElement)) continue;

        const variantId        = card.dataset.variantId ?? '';
        const variantUrl       = card.dataset.variantUrl ?? '';
        const productUrl       = card.dataset.productUrl ?? '';
        const targetUrl        = variantUrl || productUrl;
        const variantAvailable = card.dataset.variantAvailable === 'true';
        const variantImage     = card.dataset.variantImage ?? '';
        const variantImageAlt  = card.dataset.variantImageAlt ?? '';
        const variantSku       = card.dataset.variantSku ?? '';
        const variantTitle     = card.dataset.variantTitle ?? '';
        const variantPrice     = card.dataset.variantPrice ?? '';
        const variantCompareAtPrice      = card.dataset.variantCompareAtPrice ?? '';
        const variantPriceCents          = Number(card.dataset.variantPriceCents ?? '0') || 0;
        const variantCompareAtPriceCents = Number(card.dataset.variantCompareAtPriceCents ?? '0') || 0;

        // 同步所有链接 href
        if (targetUrl) {
          for (const link of card.querySelectorAll('a')) {
            if (link instanceof HTMLAnchorElement && !link.closest('filters-component')) {
              link.setAttribute('href', targetUrl);
            }
          }
        }

        // 同步变体图片
        if (variantImage) {
          const img = card.querySelector('.card-gallery img.product-media');
          if (img instanceof HTMLImageElement && img.getAttribute('src') !== variantImage) {
            img.setAttribute('src', variantImage);
            img.removeAttribute('srcset');
            if (variantImageAlt) img.setAttribute('alt', variantImageAlt);
          }
        }

        // 同步标题
        if (variantTitle) {
          const titleEl = card.querySelector(
            '[ref="productTitleLink"] p, [ref="productTitleLink"] span, ' +
            '[ref="productTitleLink"] h1, [ref="productTitleLink"] h2, ' +
            '[ref="productTitleLink"] h3, [ref="productTitleLink"] h4, ' +
            '[ref="productTitleLink"] h5, [ref="productTitleLink"] h6'
          );
          if (titleEl instanceof HTMLElement) titleEl.textContent = variantTitle;
        }

        // 同步 quick-add
        const variantInput = card.querySelector('quick-add-component input[name="id"]');
        if (variantInput instanceof HTMLInputElement && variantId) {
          variantInput.value = variantId;
          variantInput.toggleAttribute('disabled', !variantAvailable);
        }
        const quickAdd = card.querySelector('quick-add-component');
        if (quickAdd instanceof HTMLElement) quickAdd.setAttribute('data-quick-add-button', 'add');
        const addButton = card.querySelector('.quick-add__button--add');
        if (addButton instanceof HTMLButtonElement) addButton.toggleAttribute('disabled', !variantAvailable);

        // 同步 SKU
        const skuEl = card.querySelector('[data-testid="product-sku"]');
        if (skuEl instanceof HTMLElement) skuEl.textContent = variantSku;
        const skuComp = card.querySelector('product-sku-component');
        if (skuComp instanceof HTMLElement) skuComp.style.display = variantSku ? 'block' : 'none';

        // 同步价格
        const hasComparePrice = variantCompareAtPriceCents > variantPriceCents && !!variantCompareAtPrice;
        const regularWrapper  = card.querySelector('.price__regular');
        const saleWrapper     = card.querySelector('.price__sale');
        const regularEl       = card.querySelector('.price__regular .price');
        const saleEl          = card.querySelector('.price__sale .price-item--sale.price, .price__sale .price');
        const compareEl       = card.querySelector('.price__sale .price-item--regular.compare-at-price, .price__sale .compare-at-price');

        if (regularEl instanceof HTMLElement && variantPrice) regularEl.textContent = variantPrice;
        if (saleEl instanceof HTMLElement && variantPrice) saleEl.textContent = variantPrice;
        if (hasComparePrice && compareEl instanceof HTMLElement) compareEl.textContent = variantCompareAtPrice;
        if (regularWrapper instanceof HTMLElement && saleWrapper instanceof HTMLElement) {
          regularWrapper.classList.toggle('price__hidden', hasComparePrice);
          saleWrapper.classList.toggle('price__hidden', !hasComparePrice);
        }
      }
    } finally {
      this.#isSyncingVariantCards = false;
    }
  }

  #observeVariantCardGrid() {
    this.#variantCardObserver.disconnect();
    const grid = this.querySelector('[ref="grid"]');
    if (!(grid instanceof HTMLElement)) return;
    this.#variantCardObserver.observe(grid, { childList: true });
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
